import { NetworkId, FuturesTrade } from '@kwenta/sdk/types'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { isEqual, uniqWith } from 'lodash'

import { ORDER_PREVIEW_ERRORS } from 'queries/futures/constants'
import {
	DEFAULT_QUERY_STATUS,
	LOADING_STATUS,
	SUCCESS_STATUS,
	ZERO_STATE_ACCOUNT,
} from 'state/constants'
import { FetchStatus } from 'state/types'

import { PreviewAction } from '../common/types'
import { DelegationAccountInfo } from '../types'

import {
	fetchSmartMarginBalanceInfo,
	fetchSmartMarginPositions,
	fetchMarketsV2,
	fetchDailyVolumesV2,
	refetchSmartMarginPosition,
	fetchSmartMarginOpenOrders,
	fetchSmartMarginTradePreview,
	fetchSmartMarginAccount,
	fetchPositionHistoryV2,
	fetchAccountTradesForMarket,
	fetchAllV2TradesForAccount,
	fetchMarginTransfersV2,
	fetchFundingRatesHistoryV2,
	fetchFuturesFees,
	fetchFuturesFeesForAccount,
	fetchDelegatesForSMAccount,
	fetchSubAccountsForSMAccount,
	fetchSmartMarginOrdersForAccount,
	fetchPositionHistoryV2ForTrader,
	fetchSnxV2GlobalTradesForMarket,
} from './actions'
import { SMART_MARGIN_INITIAL_STATE } from './constants'
import {
	SnxPerpsV2AccountData,
	SnxPerpsV2State,
	SnxPerpsV2TradeFees,
	SnxPerpsV2TradePreview,
} from './types'

const smartMarginSlice = createSlice({
	name: 'smartMargin',
	initialState: SMART_MARGIN_INITIAL_STATE,
	reducers: {
		setOrderFeeCap: (smartMargin, action) => {
			smartMargin.orderFeeCap = action.payload
		},
		setSmartMarginFees: (smartMargin, action: PayloadAction<SnxPerpsV2TradeFees<string>>) => {
			smartMargin.fees = action.payload
		},
		setKeeperDeposit: (smartMargin, action: PayloadAction<string>) => {
			smartMargin.fees.keeperEthDeposit = action.payload
		},
		handlePreviewError: (
			smartMargin,
			{
				payload,
			}: PayloadAction<{
				error: string
				previewType: PreviewAction
			}>
		) => {
			const message = Object.values(ORDER_PREVIEW_ERRORS).includes(payload.error)
				? payload.error
				: 'Failed to get trade preview'
			smartMargin.queryStatuses.tradePreview = {
				status: FetchStatus.Error,
				error: message,
			}
			smartMargin.previews[payload.previewType] = null
		},

		setSmartMarginAccount: (
			smartMargin,
			action: PayloadAction<{ wallet: string; account: string; network: NetworkId }>
		) => {
			const { account, wallet, network } = action.payload
			if (!smartMargin.accounts[network]?.[wallet]?.account) {
				smartMargin.accounts[network] = {
					...smartMargin.accounts[network],
					[wallet]: {
						account: account,
						...ZERO_STATE_ACCOUNT,
					},
				}
			}
		},
		clearSmartMarginTradePreviews: (smartMargin) => {
			smartMargin.previews = {
				edit: null,
				trade: null,
				close: null,
			}
			smartMargin.queryStatuses.tradePreview = DEFAULT_QUERY_STATUS
		},
		setSmartMarginTradePreview: (
			smartMargin,
			{
				payload,
			}: PayloadAction<{
				preview: SnxPerpsV2TradePreview<string> | null
				type: PreviewAction
			}>
		) => {
			smartMargin.previews[payload.type] = payload.preview
		},
		setSmartMarginOrderCancelling: (
			smartMargin,
			{ payload }: PayloadAction<number | undefined>
		) => {
			smartMargin.cancellingOrder = payload
		},
		incrementSmartMarginPreviewCount: (smartMargin) => {
			smartMargin.previewDebounceCount = smartMargin.previewDebounceCount + 1
		},
		setSwapDepositSlippage: (smartMargin, action) => {
			smartMargin.swapDepositCustomSlippage = ''
			smartMargin.swapDepositSlippage = action.payload
		},
		setSwapDepositCustomSlippage: (smartMargin, action) => {
			smartMargin.swapDepositCustomSlippage = action.payload
		},
		addDelegateToSMDelegates: (
			smartMargin,
			action: PayloadAction<{
				wallet: string
				network: NetworkId
				delegate: DelegationAccountInfo
			}>
		) => {
			const { wallet, network, delegate } = action.payload
			const delegates = smartMargin.accounts[network][wallet]?.delegates ?? []
			updateSmartMarginAccount(smartMargin, network, wallet, {
				delegates: [...delegates, { ...delegate }],
			})
		},
		removeDelegateFromSMDelegates: (
			smartMargin,
			action: PayloadAction<{ wallet: string; network: NetworkId; delegatedAddress: string }>
		) => {
			const { wallet, network, delegatedAddress } = action.payload
			const delegates = smartMargin.accounts[network][wallet]?.delegates ?? []
			updateSmartMarginAccount(smartMargin, network, wallet, {
				delegates: delegates?.filter(
					({ address }) => address.toLowerCase() !== delegatedAddress.toLowerCase()
				),
			})
		},
	},
	extraReducers: (builder) => {
		// Markets
		builder.addCase(fetchMarketsV2.pending, (smartMargin) => {
			smartMargin.queryStatuses.markets = LOADING_STATUS
		})
		builder.addCase(fetchMarketsV2.fulfilled, (smartMargin, { payload }) => {
			smartMargin.queryStatuses.markets = SUCCESS_STATUS
			if (payload) {
				smartMargin.markets[payload.networkId] = payload.markets
			}
		})
		builder.addCase(fetchMarketsV2.rejected, (smartMargin) => {
			smartMargin.queryStatuses.markets = {
				status: FetchStatus.Error,
				error: 'Failed to fetch markets',
			}
		})

		// Smart margin overview
		builder.addCase(fetchSmartMarginBalanceInfo.pending, (smartMargin) => {
			smartMargin.queryStatuses.snxV2BalanceInfo = LOADING_STATUS
		})
		builder.addCase(fetchSmartMarginBalanceInfo.fulfilled, (smartMargin, action) => {
			smartMargin.queryStatuses.snxV2BalanceInfo = SUCCESS_STATUS
			if (action.payload) {
				const { account, network, balanceInfo } = action.payload
				const wallet = findWalletForAccount(smartMargin, account, network)
				if (wallet) {
					updateSmartMarginAccount(smartMargin, network, wallet, { balanceInfo })
				}
			}
		})
		builder.addCase(fetchSmartMarginBalanceInfo.rejected, (futuresState) => {
			futuresState.queryStatuses.snxV2BalanceInfo = {
				status: FetchStatus.Error,
				error: 'Failed to fetch balance info',
			}
		})

		// Daily volumes
		builder.addCase(fetchDailyVolumesV2.pending, (smartMargin) => {
			smartMargin.queryStatuses.dailyVolumes = LOADING_STATUS
		})
		builder.addCase(fetchDailyVolumesV2.fulfilled, (smartMargin, action) => {
			smartMargin.queryStatuses.dailyVolumes = SUCCESS_STATUS
			smartMargin.dailyMarketVolumes = action.payload
		})
		builder.addCase(fetchDailyVolumesV2.rejected, (smartMargin) => {
			smartMargin.queryStatuses.dailyVolumes = {
				status: FetchStatus.Error,
				error: 'Failed to fetch volume data',
			}
		})

		// margin transfers
		builder.addCase(fetchMarginTransfersV2.pending, (smartMargin) => {
			smartMargin.queryStatuses.marginTransfers = LOADING_STATUS
		})
		builder.addCase(fetchMarginTransfersV2.fulfilled, (smartMargin, { payload }) => {
			smartMargin.queryStatuses.marginTransfers = SUCCESS_STATUS
			if (payload) {
				const { context, marketMarginTransfers, accountTransfers } = payload
				const newAccountData = {
					marketMarginTransfers,
					accountTransfers,
				}
				updateSmartMarginAccount(smartMargin, context.network, context.wallet, newAccountData)
			}
		})
		builder.addCase(fetchMarginTransfersV2.rejected, (smartMargin) => {
			smartMargin.queryStatuses.marginTransfers = {
				status: FetchStatus.Error,
				error: 'Failed to fetch margin transfers',
			}
		})

		// Cross margin positions
		builder.addCase(fetchSmartMarginPositions.pending, (futuresState) => {
			futuresState.queryStatuses.positions = LOADING_STATUS
		})
		builder.addCase(fetchSmartMarginPositions.fulfilled, (smartMargin, action) => {
			smartMargin.queryStatuses.positions = SUCCESS_STATUS
			if (!action.payload) return
			const { account, positions, network } = action.payload
			const wallet = findWalletForAccount(smartMargin, account, network)
			if (wallet) {
				updateSmartMarginAccount(smartMargin, network, wallet, { positions })
			}
		})
		builder.addCase(fetchSmartMarginPositions.rejected, (smartMargin) => {
			smartMargin.queryStatuses.positions = {
				status: FetchStatus.Error,
				error: 'Failed to fetch positions',
			}
		})

		// Refetch selected position
		builder.addCase(refetchSmartMarginPosition.fulfilled, (smartMargin, { payload }) => {
			if (payload) {
				const { position, wallet, networkId } = payload
				const account = smartMargin.accounts[networkId]?.[wallet]

				const existingPositions = account?.positions ?? []
				const index = existingPositions.findIndex((p) => p.marketKey === position.marketKey)
				existingPositions[index] = position
				smartMargin.accounts[networkId][wallet] = {
					...account,
					positions: existingPositions,
				}
			}
		})

		// Fetch Cross Margin Open Orders
		builder.addCase(fetchSmartMarginOpenOrders.pending, (futuresState) => {
			futuresState.queryStatuses.openOrders = LOADING_STATUS
		})
		builder.addCase(fetchSmartMarginOpenOrders.fulfilled, (smartMargin, action) => {
			smartMargin.queryStatuses.openOrders = SUCCESS_STATUS
			if (!action.payload) return
			const { network, account, delayedOrders, conditionalOrders } = action.payload
			const wallet = findWalletForAccount(smartMargin, account, network)
			if (wallet) {
				updateSmartMarginAccount(smartMargin, network, wallet, {
					conditionalOrders,
					delayedOrders,
				})
			}
		})
		builder.addCase(fetchSmartMarginOpenOrders.rejected, (futuresState) => {
			futuresState.queryStatuses.openOrders = {
				status: FetchStatus.Error,
				error: 'Failed to fetch open orders for cross margin account',
			}
		})

		// Fetch Smart Margin Trade Preview
		builder.addCase(fetchSmartMarginTradePreview.pending, (futuresState) => {
			futuresState.queryStatuses.tradePreview = LOADING_STATUS
		})
		builder.addCase(fetchSmartMarginTradePreview.fulfilled, (smartMargin, { payload }) => {
			smartMargin.previews[payload.type] = payload.preview
			smartMargin.queryStatuses.tradePreview = SUCCESS_STATUS
		})

		// Fetch smart margin account
		builder.addCase(fetchSmartMarginAccount.pending, (futuresState) => {
			futuresState.queryStatuses.account = LOADING_STATUS
		})
		builder.addCase(fetchSmartMarginAccount.fulfilled, (smartMargin, action) => {
			if (action.payload) {
				const { network, account, wallet } = action.payload
				smartMargin.accounts[network] = {
					...smartMargin.accounts[network],
					[wallet]: {
						account: account,
						...ZERO_STATE_ACCOUNT,
					},
				}
			}
			smartMargin.queryStatuses.account = SUCCESS_STATUS
		})
		builder.addCase(fetchSmartMarginAccount.rejected, (futuresState) => {
			futuresState.queryStatuses.account = {
				status: FetchStatus.Error,
				error: 'Failed to fetch smart margin account',
			}
		})

		// Fetch position history
		builder.addCase(fetchPositionHistoryV2.pending, (futuresState) => {
			futuresState.queryStatuses.positionHistory = LOADING_STATUS
		})
		builder.addCase(fetchPositionHistoryV2.fulfilled, (futuresState, { payload }) => {
			futuresState.queryStatuses.positionHistory = SUCCESS_STATUS
			if (payload) {
				const { history: positionHistory, networkId, wallet } = payload
				updateSmartMarginAccount(futuresState, networkId, wallet, {
					positionHistory,
				})
			}
		})
		builder.addCase(fetchPositionHistoryV2.rejected, (futuresState) => {
			futuresState.queryStatuses.positionHistory = {
				error: 'Failed to fetch position history',
				status: FetchStatus.Error,
			}
		})

		// manage delegates
		builder.addCase(fetchDelegatesForSMAccount.pending, (futuresState) => {
			futuresState.queryStatuses.delegatesForSnxV2Account = LOADING_STATUS
		})
		builder.addCase(fetchDelegatesForSMAccount.fulfilled, (futuresState, { payload }) => {
			futuresState.queryStatuses.delegatesForSnxV2Account = SUCCESS_STATUS
			if (payload) {
				const { network, wallet, delegatesWithNicknames } = payload
				updateSmartMarginAccount(futuresState, network, wallet, {
					delegates: delegatesWithNicknames,
				})
			}
		})
		builder.addCase(fetchDelegatesForSMAccount.rejected, (futuresState) => {
			futuresState.queryStatuses.delegatesForSnxV2Account = {
				status: FetchStatus.Error,
				error: 'Failed to fetch delegates',
			}
		})

		// manage sub-accounts
		builder.addCase(fetchSubAccountsForSMAccount.pending, (futuresState) => {
			futuresState.queryStatuses.subAccountsForSnxV2Account = LOADING_STATUS
		})
		builder.addCase(fetchSubAccountsForSMAccount.fulfilled, (futuresState, { payload }) => {
			futuresState.queryStatuses.subAccountsForSnxV2Account = SUCCESS_STATUS
			if (payload) {
				const { network, wallet, subAccountsWithNicknames } = payload
				updateSmartMarginAccount(futuresState, network, wallet, {
					subAccounts: subAccountsWithNicknames,
				})
			}
		})
		builder.addCase(fetchSubAccountsForSMAccount.rejected, (futuresState) => {
			futuresState.queryStatuses.subAccountsForSnxV2Account = {
				status: FetchStatus.Error,
				error: 'Failed to fetch delegates',
			}
		})

		// Fetch trades for market
		builder.addCase(fetchAccountTradesForMarket.pending, (futuresState) => {
			futuresState.queryStatuses.trades = LOADING_STATUS
		})
		builder.addCase(fetchAccountTradesForMarket.fulfilled, (futuresState, { payload }) => {
			futuresState.queryStatuses.trades = SUCCESS_STATUS
			if (payload) {
				const { trades, networkId, wallet } = payload
				mergeTradesForAccount(futuresState, networkId, wallet, trades)
			}
		})
		builder.addCase(fetchAccountTradesForMarket.rejected, (futuresState) => {
			futuresState.queryStatuses.trades = {
				error: 'Failed to fetch trades',
				status: FetchStatus.Error,
			}
		})

		// TODO: Combine all with market trades rather than overwrite as the filter is done on selector

		// Fetch all trades for account
		builder.addCase(fetchAllV2TradesForAccount.pending, (futuresState) => {
			futuresState.queryStatuses.trades = LOADING_STATUS
		})
		builder.addCase(fetchAllV2TradesForAccount.fulfilled, (futuresState, { payload }) => {
			futuresState.queryStatuses.trades = SUCCESS_STATUS
			if (payload) {
				const { trades, networkId, wallet } = payload
				mergeTradesForAccount(futuresState, networkId, wallet, trades)
			}
		})
		builder.addCase(fetchAllV2TradesForAccount.rejected, (futuresState) => {
			futuresState.queryStatuses.trades = {
				error: 'Failed to fetch trades',
				status: FetchStatus.Error,
			}
		})

		// Fetch funding rates
		builder.addCase(fetchFundingRatesHistoryV2.rejected, (futuresState) => {
			futuresState.queryStatuses.historicalFundingRates = {
				error: 'Failed to fetch funding rates',
				status: FetchStatus.Error,
			}
		})
		builder.addCase(fetchFundingRatesHistoryV2.fulfilled, (futuresState, { payload }) => {
			futuresState.historicalFundingRates[payload.marketAsset] = payload.rates
		})

		builder.addCase(fetchFuturesFees.pending, (futuresState) => {
			futuresState.queryStatuses.futuresFees = LOADING_STATUS
		})
		builder.addCase(fetchFuturesFees.fulfilled, (futuresState, action) => {
			futuresState.queryStatuses.futuresFees = SUCCESS_STATUS
			futuresState.totalFuturesFeesPaid = action.payload.totalFuturesFeePaid
		})
		builder.addCase(fetchFuturesFees.rejected, (futuresState) => {
			futuresState.queryStatuses.futuresFees = {
				status: FetchStatus.Error,
				error: 'Failed to fetch fee data',
			}
		})

		// Trading Fees by given epoch and trader
		builder.addCase(fetchFuturesFeesForAccount.pending, (futuresState) => {
			futuresState.queryStatuses.futuresFeesForAccount = LOADING_STATUS
		})
		builder.addCase(fetchFuturesFeesForAccount.fulfilled, (futuresState, action) => {
			futuresState.queryStatuses.futuresFeesForAccount = SUCCESS_STATUS
			futuresState.futuresFeesForAccount = action.payload.futuresFeePaid
		})
		builder.addCase(fetchFuturesFeesForAccount.rejected, (futuresState) => {
			futuresState.queryStatuses.futuresFeesForAccount = {
				status: FetchStatus.Error,
				error: 'Failed to fetch fee data for the account',
			}
		})

		// Fetch all orders for account
		builder.addCase(fetchSmartMarginOrdersForAccount.pending, (futuresState) => {
			futuresState.queryStatuses.orderHistory = LOADING_STATUS
		})
		builder.addCase(fetchSmartMarginOrdersForAccount.fulfilled, (futuresState, { payload }) => {
			futuresState.queryStatuses.orderHistory = SUCCESS_STATUS
			if (payload) {
				const { orders: orderHistory, network, wallet } = payload
				updateSmartMarginAccount(futuresState, network, wallet, {
					orderHistory,
				})
			}
		})
		builder.addCase(fetchSmartMarginOrdersForAccount.rejected, (futuresState) => {
			futuresState.queryStatuses.orderHistory = {
				error: 'Failed to fetch orders',
				status: FetchStatus.Error,
			}
		})

		// Fetch v2 position history for trader
		builder.addCase(fetchPositionHistoryV2ForTrader.pending, (futuresState) => {
			futuresState.queryStatuses.selectedTraderPositionHistory = LOADING_STATUS
		})
		builder.addCase(fetchPositionHistoryV2ForTrader.fulfilled, (futuresState, { payload }) => {
			futuresState.queryStatuses.selectedTraderPositionHistory = SUCCESS_STATUS
			if (!payload) return
			futuresState.leaderboard.selectedTraderV2PositionHistory[payload.networkId] = {
				...futuresState.leaderboard.selectedTraderV2PositionHistory[payload.networkId],
				[payload.address]: payload.history,
			}
		})
		builder.addCase(fetchPositionHistoryV2ForTrader.rejected, (futuresState) => {
			futuresState.queryStatuses.selectedTraderPositionHistory = {
				error: 'Failed to fetch traders position history',
				status: FetchStatus.Error,
			}
		})

		// Fetch v2 global trades
		builder.addCase(fetchSnxV2GlobalTradesForMarket.pending, (futuresState) => {
			futuresState.queryStatuses.globalTradesHistory = LOADING_STATUS
		})
		builder.addCase(fetchSnxV2GlobalTradesForMarket.fulfilled, (futuresState, { payload }) => {
			futuresState.queryStatuses.globalTradesHistory = SUCCESS_STATUS
			if (!payload) return
			const trades = [
				...(futuresState.globalTradeHistory[payload.marketAsset] ?? []),
				...payload.trades,
			]
			futuresState.globalTradeHistory[payload.marketAsset] = uniqWith(
				trades.sort((a, b) => b.timestamp - a.timestamp),
				isEqual
			)
		})
		builder.addCase(fetchSnxV2GlobalTradesForMarket.rejected, (futuresState) => {
			futuresState.queryStatuses.globalTradesHistory = {
				error: 'Failed to fetch global trades history',
				status: FetchStatus.Error,
			}
		})
	},
})

export default smartMarginSlice.reducer

export const {
	handlePreviewError,
	setOrderFeeCap,
	setSmartMarginAccount,
	addDelegateToSMDelegates,
	removeDelegateFromSMDelegates,
	setSmartMarginFees,
	setKeeperDeposit,
	clearSmartMarginTradePreviews,
	setSmartMarginTradePreview,
	setSmartMarginOrderCancelling,
	incrementSmartMarginPreviewCount,
	setSwapDepositSlippage,
	setSwapDepositCustomSlippage,
} = smartMarginSlice.actions

const findWalletForAccount = (
	crossMarginState: SnxPerpsV2State,
	account: string,
	network: NetworkId
) => {
	const entry = Object.entries(crossMarginState.accounts[network]).find(([_, value]) => {
		return value.account === account
	})
	return entry ? entry[0] : undefined
}

const mergeTradesForAccount = (
	smartMarginState: SnxPerpsV2State,
	network: NetworkId,
	wallet: string,
	trades: FuturesTrade<string>[]
) => {
	const existingTrades = smartMarginState.accounts[network]?.[wallet]?.trades ?? []
	trades.forEach((t) => {
		if (!existingTrades.find((et) => et.txnHash === t.txnHash)) {
			existingTrades.push(t)
		}
	})
	existingTrades.sort((a, b) => b.timestamp - a.timestamp)
	updateSmartMarginAccount(smartMarginState, network, wallet, {
		trades: trades,
	})
}

export const updateSmartMarginAccount = (
	smartMarginState: SnxPerpsV2State,
	network: NetworkId,
	wallet: string,
	newAccountData: Partial<SnxPerpsV2AccountData>
) => {
	const updatedAccount = {
		...ZERO_STATE_ACCOUNT,
		...smartMarginState.accounts[network]?.[wallet],
		...newAccountData,
	}

	smartMarginState.accounts[network] = {
		...smartMarginState.accounts[network],
		[wallet]: updatedAccount,
	}
}
